import React, { useState, useEffect } from 'react';
import { showNotification } from './NotificationSystem';
import DocumentComparison from './DocumentComparison';
import './SimpleDocumentUpload.css';

const SimpleDocumentUpload = ({ merchantId, onDocumentUploaded, onVerificationComplete }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState('PAN_CARD');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isVerificationComplete, setIsVerificationComplete] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  const documentTypes = [
    { value: 'PAN_CARD', label: 'PAN Card', required: true, icon: '🆔' },
    { value: 'AADHAAR_CARD', label: 'Aadhaar Card', required: true, icon: '🏠' },
    { value: 'CANCELLED_CHEQUE', label: 'Cancelled Cheque', required: false, icon: '🏦' },
    { value: 'BANK_STATEMENT', label: 'Bank Statement', required: false, icon: '📄' },
    { value: 'GST_CERTIFICATE', label: 'GST Certificate', required: false, icon: '📋' },
    { value: 'BUSINESS_REGISTRATION', label: 'Business Registration', required: false, icon: '🏢' },
    { value: 'SELFIE', label: 'Live Selfie', required: true, icon: '🤳' },
    { value: 'UTILITY_BILL', label: 'Utility Bill', required: false, icon: '⚡' }
  ];

  useEffect(() => {
    fetchUploadedDocuments();
  }, [merchantId]);

  const fetchUploadedDocuments = async () => {
    try {
      // Load documents from localStorage for persistence
      const storageKey = `kyc_documents_${merchantId}`;
      const savedDocuments = localStorage.getItem(storageKey);
      if (savedDocuments) {
        setDocuments(JSON.parse(savedDocuments));
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const saveDocumentsToStorage = (updatedDocuments) => {
    try {
      const storageKey = `kyc_documents_${merchantId}`;
      localStorage.setItem(storageKey, JSON.stringify(updatedDocuments));
    } catch (error) {
      console.error('Error saving documents:', error);
    }
  };

  const handleFileSelect = async (event) => {
    console.log('File select triggered', event.target.files);
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('Selected file:', file.name, file.type, file.size);

    // Check if document type already exists
    const existingDoc = documents.find(doc => doc.documentType === selectedDocumentType);
    if (existingDoc) {
      const errorMessage = `${getDocumentTypeInfo(selectedDocumentType).label} has already been uploaded. Please delete the existing one first or select a different document type.`;
      setError(errorMessage);
      showNotification('warning', errorMessage);
      return;
    }

    // Validate file
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.error);
      console.log('File validation failed:', validation.error);
      return;
    }

    setError('');
    setSuccess('');
    setIsUploading(true);
    setUploadProgress(0);

    try {
      console.log('Starting upload simulation...');
      // Simulate upload progress
      const uploadResult = await simulateUpload(file);
      console.log('Upload completed:', uploadResult);
      
      // Simulate verification
      const verificationResult = await simulateVerification(file);
      console.log('Verification completed:', verificationResult);
      
      // Create file preview URL
      const fileUrl = URL.createObjectURL(file);
      
      // Add to documents list
      const newDocument = {
        id: Date.now().toString(),
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        documentType: selectedDocumentType,
        uploadedAt: new Date().toISOString(),
        verificationStatus: verificationResult.status,
        verificationScore: verificationResult.score,
        previewUrl: fileUrl
      };
      
      const updatedDocuments = [...documents, newDocument];
      setDocuments(updatedDocuments);
      saveDocumentsToStorage(updatedDocuments);
      const successMessage = `${getDocumentTypeInfo(selectedDocumentType).label} uploaded successfully!`;
      setSuccess(successMessage);
      showNotification('success', successMessage);
      
      if (onDocumentUploaded) {
        onDocumentUploaded(selectedDocumentType, file);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = `Upload failed: ${error.message}`;
      setError(errorMessage);
      showNotification('error', errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      // Clear the file input
      event.target.value = '';
    }
  };

  const simulateUpload = (file) => {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          setUploadProgress(100);
          setTimeout(() => resolve({ success: true }), 500);
        } else {
          setUploadProgress(Math.round(progress));
        }
      }, 200);
    });
  };

  const simulateVerification = (file) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const score = Math.floor(Math.random() * 25) + 75; // 75-100 (improved range)
        const status = score >= 75 ? 'VERIFIED' : score >= 60 ? 'NEEDS_REVIEW' : 'REJECTED';  // Lowered thresholds
        resolve({ score, status });
      }, 1000);
    });
  };

  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Only JPG, PNG, and PDF files are allowed' };
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return { isValid: false, error: 'File size must be less than 5MB' };
    }
    
    return { isValid: true };
  };

  const getDocumentTypeInfo = (type) => {
    return documentTypes.find(doc => doc.value === type) || { label: type, icon: '📄' };
  };

  const deleteDocument = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      const docToDelete = documents.find(doc => doc.id === documentId);
      if (docToDelete && docToDelete.previewUrl) {
        URL.revokeObjectURL(docToDelete.previewUrl);
      }
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      setDocuments(updatedDocuments);
      saveDocumentsToStorage(updatedDocuments);
      setSuccess('Document deleted successfully');
      setError('');
    }
  };

  const previewDoc = (document) => {
    setPreviewDocument(document);
  };

  const closePreview = () => {
    setPreviewDocument(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Drag and Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Create a fake event object to reuse handleFileSelect
      const fakeEvent = {
        target: {
          files: [file],
          value: ''
        }
      };
      await handleFileSelect(fakeEvent);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'VERIFIED': { class: 'status-verified', text: 'Verified', icon: '✅' },
      'NEEDS_REVIEW': { class: 'status-review', text: 'Needs Review', icon: '⚠️' },
      'REJECTED': { class: 'status-rejected', text: 'Rejected', icon: '❌' }
    };
    return badges[status] || badges['VERIFIED'];
  };

  const requiredDocuments = documentTypes.filter(doc => doc.required);
  const uploadedTypes = documents.map(doc => doc.documentType);
  const completionPercentage = Math.round((uploadedTypes.length / requiredDocuments.length) * 100);

  return (
    <div className="simple-document-upload">
      <div className="upload-header">
        <h3>📄 Document Upload</h3>
        <p>Upload your documents for KYC verification</p>
        
        <div className="completion-progress">
          <div className="progress-info">
            <span>Progress: {uploadedTypes.length}/{requiredDocuments.length} required documents</span>
            <span className="progress-percentage">{completionPercentage}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Document Type Selection */}
      <div className="document-type-selection">
        <h2>Select Document Type</h2>
        <div className="document-type-grid">
          {documentTypes.map(docType => (
            <button
              key={docType.value}
              className={`document-type-card ${
                selectedDocumentType === docType.value ? 'selected' : ''
              } ${
                uploadedTypes.includes(docType.value) ? 'uploaded' : ''
              }`}
              onClick={() => setSelectedDocumentType(docType.value)}
              aria-label={`Select ${docType.label} document type${uploadedTypes.includes(docType.value) ? ' (uploaded)' : ''}`}
              aria-pressed={selectedDocumentType === docType.value}
            >
              <div className="doc-icon">{docType.icon}</div>
              <div className="doc-info">
                <h5>{docType.label}</h5>
                <span className={`doc-status ${docType.required ? 'required' : 'optional'}`}>
                  {docType.required ? 'Required' : 'Optional'}
                </span>
              </div>
              {uploadedTypes.includes(docType.value) && (
                <div className="uploaded-indicator">✅</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Upload Area */}
      <div className="upload-section">
        <div className="upload-area">
          <div 
            className={`upload-label ${isDragOver ? 'drag-over' : ''}`}
            onClick={() => {
              console.log('Upload area clicked');
              document.getElementById('file-input').click();
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="upload-content">
              <div className="upload-icon">
                {isDragOver ? '📂' : '📁'}
              </div>
              <h4>
                {isDragOver 
                  ? `Drop ${getDocumentTypeInfo(selectedDocumentType).label} here` 
                  : `Upload ${getDocumentTypeInfo(selectedDocumentType).label}`
                }
              </h4>
              <p>
                {isDragOver 
                  ? 'Release to upload your file' 
                  : 'Click here or drag & drop your file'
                }
              </p>
              <small>Supports: JPG, PNG, PDF (Max 5MB)</small>
            </div>
          </div>
          
          {/* Alternative Button Upload */}
          <div className="upload-button-area">
            <button 
              className="btn btn-primary upload-btn"
              onClick={() => {
                console.log('Upload button clicked');
                document.getElementById('file-input').click();
              }}
              disabled={isUploading}
            >
              📎 Choose File to Upload
            </button>
          </div>
          
          <input 
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleFileSelect}
            disabled={isUploading}
            id="file-input"
            style={{ display: 'none' }}
          />
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className="upload-progress">
            <div className="progress-header">
              <span>Uploading {getDocumentTypeInfo(selectedDocumentType).label}...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Uploaded Documents */}
      <div className="uploaded-documents">
        <div className="documents-header">
          <h2>Uploaded Documents ({documents.length})</h2>
          {documents.length >= 2 && (
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => setShowComparison(true)}
            >
              📊 Compare Documents
            </button>
          )}
        </div>
        {documents.length === 0 ? (
          <div className="no-documents">
            <p>No documents uploaded yet</p>
            <small>Upload your first document using the area above</small>
          </div>
        ) : (
          <div className="documents-list">
            {documents.map(doc => {
              const docInfo = getDocumentTypeInfo(doc.documentType);
              const badge = getStatusBadge(doc.verificationStatus);
              
              return (
                <div key={doc.id} className="document-item">
                  <div className="document-header">
                    <div className="doc-icon">{docInfo.icon}</div>
                    <div className="doc-details">
                      <h5>{docInfo.label}</h5>
                      <small>{doc.fileName}</small>
                      <small className="file-size">{formatFileSize(doc.fileSize || 0)}</small>
                    </div>
                    <div className="document-actions">
                      <button
                        className="preview-btn"
                        onClick={() => previewDoc(doc)}
                        title="Preview document"
                      >
                        👁️
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => deleteDocument(doc.id)}
                        title="Delete document"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  <div className="document-status">
                    <span className={`status-badge ${badge.class}`}>
                      {badge.icon} {badge.text}
                    </span>
                    <span className="verification-score">
                      Score: {doc.verificationScore}%
                    </span>
                  </div>
                  
                  <div className="document-meta">
                    <small>Uploaded: {new Date(doc.uploadedAt).toLocaleString()}</small>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Complete Verification Button */}
      {documents.length > 0 && !isVerificationComplete && (
        <div className="verification-actions">
          <button
            className="btn btn-primary btn-lg"
            onClick={() => {
              setIsVerificationComplete(true);
              if (onVerificationComplete) {
                onVerificationComplete({
                  documents,
                  completionPercentage,
                  status: 'COMPLETED'
                });
              }
              setSuccess('KYC verification process initiated! Please wait for comprehensive verification to complete.');
            }}
          >
            🚀 Complete KYC Verification
          </button>
          <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
            This will start the comprehensive verification process including document authenticity, data extraction, and face verification.
          </p>
        </div>
      )}

      {/* Verification Complete Message */}
      {isVerificationComplete && (
        <div className="verification-complete">
          <div className="alert alert-success">
            <h3>✅ Documents Submitted Successfully!</h3>
            <p>Your documents have been uploaded and the verification process has been initiated. Please use the "Start Comprehensive Verification" button above to begin the automated verification process.</p>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <div 
          className="preview-modal" 
          onClick={closePreview}
          role="button"
          tabIndex={0}
          aria-label="Close preview"
          onKeyDown={(e) => e.key === 'Escape' && closePreview()}
        >
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <h3>📄 {getDocumentTypeInfo(previewDocument.documentType).label}</h3>
              <button className="close-preview" onClick={closePreview} aria-label="Close document preview">✕</button>
            </div>
            <div className="preview-body">
              {previewDocument.fileType?.startsWith('image/') ? (
                <img 
                  src={previewDocument.previewUrl} 
                  alt={previewDocument.fileName}
                  className="preview-image"
                />
              ) : (
                <div className="preview-placeholder">
                  <div className="file-icon">📄</div>
                  <h4>{previewDocument.fileName}</h4>
                  <p>PDF files cannot be previewed directly</p>
                  <small>Size: {formatFileSize(previewDocument.fileSize || 0)}</small>
                </div>
              )}
            </div>
            <div className="preview-footer">
              <div className="preview-info">
                <p><strong>File:</strong> {previewDocument.fileName}</p>
                <p><strong>Size:</strong> {formatFileSize(previewDocument.fileSize || 0)}</p>
                <p><strong>Status:</strong> {getStatusBadge(previewDocument.verificationStatus).text}</p>
                <p><strong>Score:</strong> {previewDocument.verificationScore}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Comparison Modal */}
      {showComparison && (
        <DocumentComparison 
          documents={documents}
          onClose={() => setShowComparison(false)}
        />
      )}
    </div>
  );
};

export default SimpleDocumentUpload;